import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import ImageDropZone from '../common/ImageDropZone';
import { uploadFile } from '../../services/upload.service';

/**
 * Lead magnets and LM Live Events as initiatives (To-Do 84299506, Campaigns
 * step (a), Jenn Oct 7 2026). Jenn: "This system is supposed to be a one place
 * stop" — so each lead magnet / event holds its own plan, budget, deliverables
 * (with what's involved), pictures and files, tracked links, numbers and
 * nudges, right on its own page. No separate Campaigns page.
 *
 * Rendered inside the Sales & Marketing Control Room (.smcr styles).
 * IdeasList is the marketing / advertising / publicity ideas list Jenn and
 * Claude grow over time; picking an idea adds it as a piece.
 */

const WORDS = {
  lead_magnet: { one: 'lead magnet', many: 'lead magnets', Many: 'Your lead magnets', page: 'Lead magnets' },
  live_event: { one: 'event', many: 'events', Many: 'Your LM live events', page: 'LM Live Events' },
};
const STATUS = { planning: ['Planning', ''], running: ['Running', 'g'], done: ['Finished', 'a'] };
const D_STATUS = { todo: ['To do', ''], doing: ['Doing', 'a'], done: ['Done', 'g'] };
const GROUPS = [['marketing', 'Marketing'], ['advertising', 'Advertising'], ['publicity', 'Publicity']];
const KIND_SUGGESTIONS = ['Ad', 'Publicity', 'Tour stop', 'Social post', 'Email', 'Affiliate push', 'Partner push', 'Landing page'];

const $ = (n) => (n == null ? '—' : `$${Number(n).toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`);
const day = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) : null);
const errMsg = (e) => e?.response?.data?.error || 'Could not save that. Please try again.';

// A writing box that grows to show everything in it, so long notes can be read and edited
// without scrolling inside a tiny box (Jenn, 2026-10-21).
function GrowBox({ value, onChange, minRows = 3, ...rest }) {
  const ref = React.useRef(null);
  React.useLayoutEffect(() => {
    const el = ref.current; if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [value]);
  return <textarea ref={ref} rows={minRows} value={value} onChange={onChange} style={{ overflow: 'hidden', resize: 'vertical', lineHeight: 1.5 }} {...rest} />;
}

const Field = ({ label, children, wide }) => <label className="fld" style={wide ? { gridColumn: '1 / -1' } : undefined}>{label}{children}</label>;

function Numbers({ n, small }) {
  const cells = [['Clicks', n.clicks], ['Visits', n.visits], ['Started signing up', n.signups], ['Joined', n.joins]];
  return (
    <div className="row" style={{ gap: small ? 14 : 22, marginTop: small ? 6 : 10 }}>
      {cells.map(([l, v]) => (
        <div key={l}><div style={{ fontSize: small ? 15 : 22, fontWeight: 700 }}>{v}</div><div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{l}</div></div>
      ))}
    </div>
  );
}

const TERM_WORD = { week: ['a week', 'weeks'], month: ['a month', 'months'], year: ['a year', 'years'] };
const costLabel = (d) => (!TERM_WORD[d.costTerm] ? $(d.cost)
  : `${$(d.cost)} ${TERM_WORD[d.costTerm][0]}${d.costTimes > 1 ? ` × ${d.costTimes} ${TERM_WORD[d.costTerm][1]} = ${$(d.costTotal)}` : ''}`);

function BudgetBar({ spent, budget }) {
  if (!budget) return <div className="qs" style={{ margin: '6px 0 0' }}>Spent {$(spent)} · no budget set</div>;
  const pct = Math.min(100, Math.round((spent / budget) * 100));
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ height: 8, borderRadius: 99, background: 'var(--sand)', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: spent > budget ? 'var(--terra)' : 'var(--forest, #4A6355)' }} />
      </div>
      <div className="qs" style={{ margin: '4px 0 0' }}>Spent {$(spent)} of {$(budget)} budget</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The list on the Lead magnets / LM Live Events page
// ---------------------------------------------------------------------------

export default function MkInitiatives({ kind }) {
  const w = WORDS[kind];
  const [items, setItems] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    api.get('/admin/sales-marketing/initiatives', { params: { kind } })
      .then(r => { setItems(r.data.initiatives); setErr(null); })
      .catch(() => setErr(`Could not load your ${w.many}.`));
  }, [kind, w.many]);
  useEffect(() => { load(); }, [load]);

  if (openId) return <Initiative id={openId} kind={kind} onBack={() => { setOpenId(null); load(); }} />;

  return (
    <div style={{ marginBottom: 24 }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h3 className="q" style={{ fontSize: 19 }}>{w.Many}</h3>
        {!adding && <button type="button" className="btn p" onClick={() => setAdding(true)}>+ Add a {w.one}</button>}
      </div>
      {err && <div className="note">{err}</div>}
      {adding && (
        <div className="card" style={{ marginTop: 12 }}>
          <p className="q">New {w.one}</p>
          <PlanForm kind={kind} initial={{ status: 'planning' }} saveLabel={`Add ${w.one}`}
            onCancel={() => setAdding(false)}
            onSave={async (body) => { const r = await api.post('/admin/sales-marketing/initiatives', { ...body, kind }); setAdding(false); setOpenId(r.data.id); }} />
        </div>
      )}
      {items && items.length === 0 && !adding && (
        <div className="note">No {w.many} planned here yet. Press <b>+ Add a {w.one}</b> to plan one: its dates, budget and every piece that goes with it.</div>
      )}
      {items && items.length > 0 && (
        <div className="grid g3" style={{ marginTop: 12 }}>
          {items.map(i => (
            <div key={i.id} className="card go" role="button" tabIndex={0} onClick={() => setOpenId(i.id)} onKeyDown={e => { if (e.key === 'Enter') setOpenId(i.id); }}>
              <span className={`tag ${STATUS[i.status][1]}`}>{STATUS[i.status][0]}</span>
              <p className="q" style={{ marginTop: 8 }}>{i.name}</p>
              <p className="qs" style={{ marginBottom: 6 }}>
                {[kind === 'live_event' && i.eventAt, i.startsOn && `${day(i.startsOn)}${i.endsOn ? ` – ${day(i.endsOn)}` : ''}`].filter(Boolean).join(' · ') || 'No dates yet'}
                {' · '}{i.doneCount} of {i.deliverableCount} pieces done
              </p>
              <BudgetBar spent={i.spent} budget={i.budget} />
              <Numbers n={i.numbers} small />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The plan (name, offer, goal, audience, dates, budget, status)
// ---------------------------------------------------------------------------

function PlanForm({ kind, initial, onSave, onCancel, saveLabel = 'Save' }) {
  const [f, setF] = useState({ name: '', offer: '', goal: '', audience: '', startsOn: '', endsOn: '', eventAt: '', budget: '', status: 'planning', notes: '', ...initial });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k) => (e) => setF(x => ({ ...x, [k]: e.target.value }));
  const submit = async () => {
    setBusy(true); setErr(null);
    try { await onSave({ ...f, budget: f.budget === '' || f.budget == null ? null : f.budget }); } catch (e) { setErr(errMsg(e)); setBusy(false); }
  };
  return (
    <div style={{ marginTop: 10 }}>
      <div className="ws">
        <Field label="Name"><input value={f.name || ''} onChange={set('name')} placeholder={kind === 'live_event' ? 'e.g. The Ten-Minute Morning' : 'e.g. 5-day Sleep Reset'} /></Field>
        <Field label="Status"><select value={f.status} onChange={set('status')}>{Object.entries(STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}</select></Field>
        <Field label="What people get (the offer)" wide><GrowBox value={f.offer || ''} onChange={set('offer')} /></Field>
        <Field label="Goal" wide><GrowBox minRows={1} value={f.goal || ''} onChange={set('goal')} placeholder="e.g. 15 new members" /></Field>
        <Field label="Who it's for" wide><GrowBox minRows={1} value={f.audience || ''} onChange={set('audience')} /></Field>
        {kind === 'live_event' && <Field label="Event date and time" wide><input value={f.eventAt || ''} onChange={set('eventAt')} placeholder="e.g. Tuesday Nov 10 · 2:00 pm Atlantic" /></Field>}
        <Field label="Campaign starts"><input type="date" value={f.startsOn || ''} onChange={set('startsOn')} /></Field>
        <Field label="Campaign ends"><input type="date" value={f.endsOn || ''} onChange={set('endsOn')} /></Field>
        <Field label="Budget (CA$)"><input type="number" min="0" step="0.01" value={f.budget ?? ''} onChange={set('budget')} /></Field>
        <Field label="Notes" wide><GrowBox minRows={6} value={f.notes || ''} onChange={set('notes')} /></Field>
      </div>
      {err && <div className="note">{err}</div>}
      <div className="row" style={{ marginTop: 12 }}>
        <button type="button" className="btn p" disabled={busy} onClick={submit}>{busy ? 'Saving…' : saveLabel}</button>
        {onCancel && <button type="button" className="btn" onClick={onCancel}>Cancel</button>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Files on the lead magnet / event itself (Jenn, 2026-10-13) — e.g. Juli's
// slide deck and worksheet. Upload a file or add a Drive link; saved at once.
// ---------------------------------------------------------------------------

function FilesBox({ id, files: initial }) {
  const [files, setFiles] = useState(initial || []);
  const [link, setLink] = useState({ name: '', url: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const inputRef = React.useRef(null);
  const save = async (next) => {
    setBusy(true); setMsg(null);
    try { const r = await api.put(`/admin/sales-marketing/initiatives/${id}/files`, { files: next }); setFiles(r.data.files); }
    catch (e) { setMsg(errMsg(e)); } finally { setBusy(false); }
  };
  const onPick = async (e) => {
    const picked = [...(e.target.files || [])];
    e.target.value = '';
    if (!picked.length) return;
    setBusy(true); setMsg(null);
    try {
      const added = [];
      for (const f of picked) { const r = await uploadFile(f); added.push({ name: r.name || f.name, url: r.url }); }
      await save([...files, ...added]);
    } catch (err) { setMsg(errMsg(err)); setBusy(false); }
  };
  const addLink = () => {
    const url = link.url.trim();
    if (!/^https?:\/\//i.test(url)) { setMsg('Paste a full link, starting with https://'); return; }
    save([...files, { name: link.name.trim() || url, url }]); setLink({ name: '', url: '' });
  };
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <p className="q">Files</p>
      <p className="qs">Slide decks, worksheets, notes from partners. Upload a file or add a Google Drive link.</p>
      {files.length === 0 ? <div className="empty">No files yet.</div> : (
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 10px' }}>
          {files.map((x, i) => (
            <li key={`${x.url}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--line, #eee)' }}>
              <a href={x.url} target="_blank" rel="noopener noreferrer" style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{x.name}</a>
              <button type="button" className="btn s" disabled={busy} onClick={() => { if (window.confirm(`Remove "${x.name}" from this list?`)) save(files.filter((_, j) => j !== i)); }}>Remove</button>
            </li>
          ))}
        </ul>
      )}
      <div className="row">
        <input ref={inputRef} type="file" multiple style={{ display: 'none' }} onChange={onPick}
          accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.csv,image/*" />
        <button type="button" className="btn p s" disabled={busy} onClick={() => inputRef.current?.click()}>{busy ? 'Working…' : 'Upload a file'}</button>
      </div>
      <div className="ws" style={{ marginTop: 10 }}>
        <Field label="Or add a link — name"><input value={link.name} onChange={e => setLink(l => ({ ...l, name: e.target.value }))} placeholder="e.g. Juli's slide deck (Drive)" /></Field>
        <Field label="Link"><input value={link.url} onChange={e => setLink(l => ({ ...l, url: e.target.value }))} placeholder="https://drive.google.com/…" /></Field>
      </div>
      <div className="row" style={{ marginTop: 8 }}><button type="button" className="btn s" disabled={busy || !link.url.trim()} onClick={addLink}>Add link</button></div>
      {msg && <div className="note">{msg}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sign-up page + RSVP list for a live event (Jenn, 2026-10-14: "a template we
// can use for any event we do"). One public template at
// 365reset.com/event/<address>, filled from these words. Never a Zoom link.
// ---------------------------------------------------------------------------

const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

// <input type="datetime-local"> works in the browser's own time; Team365 is on Atlantic time, so show and save it that way.
const toLocalInput = (iso) => {
  if (!iso) return '';
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Halifax', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    .formatToParts(new Date(iso)).map(x => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};
const fromAtlantic = (v) => {
  if (!v) return null;
  const guess = new Date(`${v}:00Z`);
  const off = new Date(guess.toLocaleString('en-US', { timeZone: 'America/Halifax' })) - new Date(guess.toLocaleString('en-US', { timeZone: 'UTC' }));
  return new Date(guess.getTime() - off).toISOString();
};
const atlantic = (iso) => new Date(iso).toLocaleString('en-CA', { timeZone: 'America/Halifax', weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) + ' Atlantic';
const lines = (t) => String(t || '').split('\n').map(x => x.trim()).filter(Boolean);

function SignupBox({ id }) {
  const [data, setData] = useState(null);
  const [f, setF] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const load = useCallback(() => {
    api.get(`/admin/sales-marketing/initiatives/${id}/signups`).then(r => setData(r.data)).catch(e => setMsg(errMsg(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (!data) return msg ? <div className="note">{msg}</div> : null;
  const s = data.settings;
  const startEdit = () => {
    const pg = s.page || {};
    const faces = [...(pg.faces || []), {}, {}].slice(0, Math.max(2, (pg.faces || []).length));
    setF({ open: s.open, slug: s.slug || data.defaults.slug, title: s.title || '', about: s.about || data.defaults.about || '', hosts: s.hosts || '', partnerName: s.partnerName || '',
      startsAt: toLocalInput(s.startsAt), joinUrl: s.joinUrl || '', replayUrl: s.replayUrl || '',
      look: !!s.page, eyebrow: pg.eyebrow || 'Free live workshop on Zoom', steps: (pg.steps || []).join(', '), introHeading: pg.introHeading || '',
      bulletsHeading: pg.bulletsHeading || "You'll leave with", bullets: (pg.bullets || []).join('\n'),
      faces: faces.map(x => ({ name: x.name || '', title: x.title || '', photo: x.photo || '', alt: x.alt || '' })),
      logoText: pg.partnerLogo?.text || '', logoUrl: pg.partnerLogo?.url || '' });
    setEditing(true); setMsg(null);
  };
  const save = async (body) => {
    setBusy(true); setMsg(null);
    if ('look' in body) {
      body = { ...body, startsAt: fromAtlantic(body.startsAt),
        page: body.look ? { eyebrow: body.eyebrow, steps: String(body.steps).split(',').map(x => x.trim()).filter(Boolean), introHeading: body.introHeading,
          intro: body.about, bulletsHeading: body.bulletsHeading, bullets: lines(body.bullets), faces: body.faces.filter(x => x.name.trim()),
          partnerLogo: body.logoText.trim() ? { text: body.logoText, url: body.logoUrl } : null } : null };
    }
    try { const r = await api.put(`/admin/sales-marketing/initiatives/${id}/signup-page`, body); setData(r.data); setEditing(false); }
    catch (e) { setMsg(errMsg(e)); } finally { setBusy(false); }
  };
  const current = { open: s.open, slug: s.slug, title: s.title, about: s.about, hosts: s.hosts, partnerName: s.partnerName };
  const download = () => {
    const head = ['First name', 'Last name', 'Email', 'Mobile', 'Wants texts', 'Signed up', 'Wants 365reset emails', `Share with ${s.partnerName || 'partner'}`, 'Question', 'Came from'];
    const rows = data.signups.map(p => [p.firstName, p.lastName, p.email, p.phone, p.wantsTexts ? 'yes' : 'no', new Date(p.createdAt).toLocaleString(), p.wantsEmails ? 'yes' : 'no', p.shareWithPartner ? 'yes' : 'no', p.question, p.source].map(csvCell).join(','));
    const blob = new Blob([[head.map(csvCell).join(','), ...rows].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `sign-ups-${s.slug || 'event'}.csv`; a.click();
  };
  const shared = data.signups.filter(p => p.shareWithPartner).length;
  const set = (k) => (e) => setF(x => ({ ...x, [k]: e.target.value }));
  const setFace = (i, k, v) => setF(x => ({ ...x, faces: x.faces.map((fc, j) => (j === i ? { ...fc, [k]: v } : fc)) }));
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <p className="q" style={{ margin: 0 }}>Sign-up page</p>
        <span className={`tag ${s.open ? 'g' : ''}`}>{s.open ? 'Open for sign-ups' : 'Not open'}</span>
      </div>
      {!editing ? (
        <>
          {s.url ? (
            <p style={{ margin: '8px 0', overflowWrap: 'anywhere' }}>
              <a href={s.url} target="_blank" rel="noopener noreferrer">{s.url}</a>
              {!s.open && <span className="qs"> (people see "not taking sign-ups" until you open it)</span>}
            </p>
          ) : <p className="qs">No page yet. Click "Set up the page" — the words start from this event's plan, and you can change them.</p>}
          {s.previewUrl && !s.open && <p className="qs" style={{ margin: '4px 0', overflowWrap: 'anywhere' }}>See it before it opens (share with your partner): <a href={s.previewUrl} target="_blank" rel="noopener noreferrer">preview link</a></p>}
          <p className="qs" style={{ margin: '4px 0' }}>
            Reminders: {s.startsAt ? `starts ${atlantic(s.startsAt)}` : 'add the start time'} · {s.joinUrl ? 'join link in' : 'join link not in yet (reminders wait for it)'} · {s.replayUrl ? 'replay link in' : 'replay link not in yet (the thank-you waits for it)'}
          </p>
          {s.partnerName && <p className="qs" style={{ margin: '4px 0' }}>People can tick "share my name and email with {s.partnerName}". It's never ticked for them.</p>}
          <div className="row" style={{ marginTop: 10 }}>
            <button type="button" className="btn s" onClick={startEdit}>{s.url ? 'Edit the page' : 'Set up the page'}</button>
            {s.url && <button type="button" className="btn s" disabled={busy} onClick={() => save({ ...current, open: !s.open })}>{s.open ? 'Close sign-ups' : 'Open sign-ups'}</button>}
          </div>
        </>
      ) : (
        <div className="ws" style={{ marginTop: 10 }}>
          <Field label="Web address" wide>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span className="qs" style={{ whiteSpace: 'nowrap' }}>365reset.com/event/</span>
              <input value={f.slug} onChange={set('slug')} placeholder="e.g. falling-off-the-plan" /></div>
          </Field>
          <Field label="Big heading (blank = the event name)" wide><input value={f.title} onChange={set('title')} placeholder={data.defaults.title} /></Field>
          <Field label="Who's leading it (optional)" wide><input value={f.hosts} onChange={set('hosts')} placeholder="e.g. With Juli Colotti and Jenn Humphries" /></Field>
          <Field label="What it is and what people leave with" wide><textarea rows={6} value={f.about} onChange={set('about')} /></Field>
          <Field label="Partner to share sign-ups with (optional)" wide><input value={f.partnerName} onChange={set('partnerName')} placeholder="e.g. Juli Colotti — leave blank for no share box" /></Field>
          <Field label="Starts (Atlantic time)"><input type="datetime-local" value={f.startsAt} onChange={set('startsAt')} /></Field>
          <Field label="Zoom join link (only ever sent by email and text)"><input value={f.joinUrl} onChange={set('joinUrl')} placeholder="https://us02web.zoom.us/j/..." /></Field>
          <Field label="Replay link (for the thank-you email)" wide><input value={f.replayUrl} onChange={set('replayUrl')} placeholder="Add after the event — the thank-you goes out once it's in" /></Field>
          <p className="qs" style={{ gridColumn: '1 / -1', margin: 0 }}>Reminders go out by themselves: the day before and an hour before (email, and text for people who ticked it), then a thank-you with the replay.</p>
          <label className="row" style={{ gridColumn: '1 / -1', gap: 8 }}><input type="checkbox" checked={f.look} onChange={e => setF(x => ({ ...x, look: e.target.checked }))} /> Use the banner look (photos, logos, bullets)</label>
          {f.look && <>
            <Field label="Small words above the heading"><input value={f.eyebrow} onChange={set('eyebrow')} /></Field>
            <Field label="Steps in the banner (commas between)"><input value={f.steps} onChange={set('steps')} placeholder="Park It, Place It, Practice It" /></Field>
            <Field label="Heading above the description (optional)" wide><input value={f.introHeading} onChange={set('introHeading')} /></Field>
            <Field label="Heading above the bullets"><input value={f.bulletsHeading} onChange={set('bulletsHeading')} /></Field>
            <Field label="Bullets (one per line)" wide><textarea rows={5} value={f.bullets} onChange={set('bullets')} /></Field>
            {f.faces.map((fc, i) => (
              <div key={i} style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 8, borderTop: '1px solid var(--line, #eee)', paddingTop: 8 }}>
                <Field label={`Person ${i + 1}: name`}><input value={fc.name} onChange={e => setFace(i, 'name', e.target.value)} /></Field>
                <Field label="Their title"><input value={fc.title} onChange={e => setFace(i, 'title', e.target.value)} /></Field>
                <div className="fld" style={{ gridColumn: '1 / -1' }}>Photo<div style={{ maxWidth: 280 }}><ImageDropZone currentUrl={fc.photo ? (fc.photo.startsWith('/') ? `https://365reset.com${fc.photo}` : fc.photo) : null} onUploaded={(u) => setFace(i, 'photo', u)} onClear={() => setFace(i, 'photo', '')} /></div></div>
                <Field label="Photo description, for people using screen readers" wide><input value={fc.alt} onChange={e => setFace(i, 'alt', e.target.value)} placeholder={`e.g. ${fc.name || 'Juli'} smiling`} /></Field>
              </div>
            ))}
            <div style={{ gridColumn: '1 / -1' }}><button type="button" className="btn s" onClick={() => setF(x => ({ ...x, faces: [...x.faces, { name: '', title: '', photo: '', alt: '' }] }))}>Add a person</button></div>
            <Field label="Partner's name as a logo, lower left (optional)"><input value={f.logoText} onChange={set('logoText')} placeholder="e.g. Coach Juli" /></Field>
            <Field label="Partner's website"><input value={f.logoUrl} onChange={set('logoUrl')} placeholder="https://..." /></Field>
          </>}
          <label className="row" style={{ gridColumn: '1 / -1', gap: 8 }}><input type="checkbox" checked={f.open} onChange={e => setF(x => ({ ...x, open: e.target.checked }))} /> Open for sign-ups</label>
          <p className="qs" style={{ gridColumn: '1 / -1', margin: 0 }}>The date and time come from the plan above. The page never shows a Zoom link — people get it by email.</p>
          <div className="row" style={{ gridColumn: '1 / -1' }}>
            <button type="button" className="btn p s" disabled={busy} onClick={() => save(f)}>{busy ? 'Saving…' : 'Save'}</button>
            <button type="button" className="btn s" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}
      {msg && <div className="note">{msg}</div>}

      <div className="row" style={{ justifyContent: 'space-between', marginTop: 18 }}>
        <p className="q" style={{ margin: 0, fontSize: 16 }}>Who's coming ({data.signups.length})</p>
        {data.signups.length > 0 && <button type="button" className="btn s" onClick={download}>Download the list</button>}
      </div>
      {data.signups.length === 0 ? <div className="empty">No sign-ups yet.</div> : (
        <>
          <p className="qs" style={{ margin: '4px 0 8px' }}>
            {data.signups.filter(p => p.wantsEmails).length} said yes to 365reset emails{s.partnerName ? ` · ${shared} said yes to sharing with ${s.partnerName}` : ''}
          </p>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {data.signups.map(p => (
              <li key={p.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--line, #eee)' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <b>{p.firstName}{p.lastName ? ` ${p.lastName}` : ''}</b><span style={{ overflowWrap: 'anywhere' }}>{p.email}</span>
                  {p.phone && <span>{p.phone}</span>}
                  {p.wantsTexts && <span className="tag g">Texts yes</span>}
                  {p.wantsEmails && <span className="tag g">Emails yes</span>}
                  {p.shareWithPartner && <span className="tag">Share yes</span>}
                  <span className="qs" style={{ marginLeft: 'auto' }}>{new Date(p.createdAt).toLocaleDateString()}</span>
                  <button type="button" className="btn s" onClick={async () => {
                    if (!window.confirm(`Take ${p.firstName} off this list?`)) return;
                    try { const r = await api.delete(`/admin/sales-marketing/initiatives/${id}/signups/${p.id}`); setData(r.data); } catch (e) { setMsg(errMsg(e)); }
                  }}>Remove</button>
                </div>
                {p.question && <p className="qs" style={{ margin: '4px 0 0' }}>"{p.question}"</p>}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One lead magnet / event, opened
// ---------------------------------------------------------------------------

function Initiative({ id, kind, onBack }) {
  const w = WORDS[kind];
  const [ini, setIni] = useState(null);
  const [editing, setEditing] = useState(false);
  const [openPiece, setOpenPiece] = useState(null); // deliverable id, or 'new'
  const [newDraft, setNewDraft] = useState(null);
  const [picking, setPicking] = useState(false);
  const [posts, setPosts] = useState([]);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/sales-marketing/initiatives/${id}`).then(r => setIni(r.data)).catch(() => setErr(`Could not load this ${w.one}.`));
  }, [id, w.one]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/admin/sales-marketing/attachable-posts').then(r => setPosts(r.data.posts)).catch(() => {}); }, []);

  if (err) return <div className="note">{err} <button type="button" className="btn s" onClick={onBack}>Back</button></div>;
  if (!ini) return <p className="qs">Loading…</p>;

  const startNew = (draft) => { setNewDraft(draft); setOpenPiece('new'); setPicking(false); };

  return (
    <div style={{ marginBottom: 24 }}>
      <button type="button" className="btn s" onClick={onBack}>← All {w.many}</button>

      <div className="grid g2" style={{ marginTop: 12 }}>
        <div className="card" style={editing ? { gridColumn: '1 / -1' } : undefined}>
          <span className={`tag ${STATUS[ini.status][1]}`}>{STATUS[ini.status][0]}</span>
          <p className="q" style={{ marginTop: 8, fontSize: 21 }}>{ini.name}</p>
          {!editing ? (
            <>
              <p className="qs" style={{ marginBottom: 8 }}>
                {[kind === 'live_event' && ini.eventAt, ini.startsOn && `Campaign ${day(ini.startsOn)}${ini.endsOn ? ` – ${day(ini.endsOn)}` : ''}`].filter(Boolean).join(' · ') || 'No dates yet'}
              </p>
              {ini.offer && <p style={{ margin: '4px 0' }}><b>What people get:</b> {ini.offer}</p>}
              {ini.goal && <p style={{ margin: '4px 0' }}><b>Goal:</b> {ini.goal}</p>}
              {ini.audience && <p style={{ margin: '4px 0' }}><b>Who it's for:</b> {ini.audience}</p>}
              {ini.notes && <p style={{ margin: '4px 0', whiteSpace: 'pre-wrap' }}><b>Notes:</b> {ini.notes}</p>}
              <BudgetBar spent={ini.spent} budget={ini.budget} />
              <div className="row" style={{ marginTop: 12 }}>
                <button type="button" className="btn s" onClick={() => setEditing(true)}>Edit the plan</button>
                <button type="button" className="btn s" onClick={async () => {
                  if (!window.confirm(`Put "${ini.name}" away? It's kept (not deleted), and its links keep working.`)) return;
                  await api.post(`/admin/sales-marketing/initiatives/${id}/archive`, { archived: true }); onBack();
                }}>Put away</button>
              </div>
            </>
          ) : (
            <PlanForm kind={kind} initial={ini} onCancel={() => setEditing(false)}
              onSave={async (body) => { const r = await api.put(`/admin/sales-marketing/initiatives/${id}`, body); setIni(r.data); setEditing(false); }} />
          )}
        </div>
        <div className="card">
          <p className="q">How it's doing</p>
          <p className="qs">All pieces together, from their tracked links and attached posts</p>
          <Numbers n={ini.numbers} />
          <div className="note">{ini.doneCount} of {ini.deliverableCount} pieces done. A piece that reaches its due date without being marked done shows up on To-Do's.</div>
        </div>
      </div>

      <FilesBox key={ini.id} id={ini.id} files={ini.files} />
      {kind === 'live_event' && <SignupBox key={`s-${ini.id}`} id={ini.id} />}

      <div className="row" style={{ justifyContent: 'space-between', marginTop: 20 }}>
        <h3 className="q" style={{ fontSize: 18 }}>The pieces</h3>
        <div className="row">
          <button type="button" className="btn p" onClick={() => startNew({ title: '', items: [] })}>+ Add a piece</button>
          <button type="button" className="btn" onClick={() => setPicking(p => !p)}>{picking ? 'Close ideas' : 'Pick from the ideas list'}</button>
        </div>
      </div>

      {picking && (
        <div className="card" style={{ marginTop: 12 }}>
          <IdeasList onPick={(idea) => startNew({ title: idea.name, kind: idea.name, ideaId: idea.id, items: idea.involves.map(label => ({ label, done: false })) })} />
        </div>
      )}

      {openPiece === 'new' && (
        <Piece draft={newDraft} posts={posts} isNew
          onCancel={() => setOpenPiece(null)}
          onSave={async (body) => { const r = await api.post(`/admin/sales-marketing/initiatives/${id}/deliverables`, body); setIni(r.data); setOpenPiece(null); }} />
      )}

      {ini.deliverables.length === 0 && openPiece !== 'new' && (
        <div className="note">No pieces yet. Add the ads, posts, emails, tour stops, merchandise, or anything else this {w.one} needs. Each piece gets its own tracked link, made for you.</div>
      )}

      {ini.deliverables.map(d => (openPiece === d.id ? (
        <Piece key={d.id} draft={d} posts={posts}
          onCancel={() => setOpenPiece(null)}
          onSave={async (body) => { const r = await api.put(`/admin/sales-marketing/deliverables/${d.id}`, body); setIni(r.data); setOpenPiece(null); }}
          onDelete={async () => {
            if (!window.confirm(`Remove "${d.title}"? Its cost comes off the marketing costs too. Its link keeps working.`)) return;
            const r = await api.delete(`/admin/sales-marketing/deliverables/${d.id}`); setIni(r.data); setOpenPiece(null);
          }} />
      ) : (
        <div key={d.id} className="card go" style={{ marginTop: 10, padding: 16 }} role="button" tabIndex={0}
          onClick={() => setOpenPiece(d.id)} onKeyDown={e => { if (e.key === 'Enter') setOpenPiece(d.id); }}>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <div className="row">
              <span className={`tag ${D_STATUS[d.status][1]}`}>{D_STATUS[d.status][0]}</span>
              <b>{d.title}</b>
              {d.kind && d.kind !== d.title && <span className="qs" style={{ margin: 0 }}>{d.kind}</span>}
            </div>
            <span className="qs" style={{ margin: 0 }}>
              {[d.dueOn && `Due ${day(d.dueOn)}`, d.owner, d.cost != null && costLabel(d), d.items.length > 0 && `${d.items.filter(x => x.done).length}/${d.items.length} ready`].filter(Boolean).join(' · ')}
            </span>
          </div>
          <Numbers n={d.numbers} small />
        </div>
      )))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One piece (deliverable), being added or edited
// ---------------------------------------------------------------------------

function Piece({ draft, posts, isNew, onSave, onCancel, onDelete }) {
  const [f, setF] = useState({
    title: '', kind: '', dueOn: '', status: 'todo', owner: '', cost: '', costTerm: 'once', notes: '', targetUrl: 'https://365reset.com/',
    items: [], files: [], socialLinkCodes: [], ...draft,
  });
  const [newItem, setNewItem] = useState('');
  const [link, setLink] = useState({ name: '', url: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);
  const set = (k) => (e) => setF(x => ({ ...x, [k]: e.target.value }));
  const setItems = (fn) => setF(x => ({ ...x, items: fn(x.items) }));

  const addItem = () => { const l = newItem.trim(); if (!l) return; setItems(it => [...it, { label: l, done: false }]); setNewItem(''); };
  const addLink = () => {
    const url = link.url.trim();
    if (!/^https?:\/\//i.test(url)) { setErr('A link starts with https://'); return; }
    setF(x => ({ ...x, files: [...x.files, { name: link.name.trim() || url, url }] })); setLink({ name: '', url: '' }); setErr(null);
  };
  const submit = async () => {
    setBusy(true); setErr(null);
    // Anything typed in the "add" boxes but not yet added is kept, not lost.
    const items = newItem.trim() ? [...f.items, { label: newItem.trim(), done: false }] : f.items;
    try { await onSave({ ...f, items, cost: f.cost === '' || f.cost == null ? null : f.cost }); } catch (e) { setErr(errMsg(e)); setBusy(false); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(f.shortUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* fine */ } };
  const unattached = posts.filter(p => !f.socialLinkCodes.includes(p.code));

  return (
    <div className="card" style={{ marginTop: 10, borderColor: 'var(--terra-2)' }}>
      <p className="q">{isNew ? 'New piece' : f.title}</p>
      <div className="ws" style={{ marginTop: 10 }}>
        <Field label="What it is"><input value={f.title} onChange={set('title')} placeholder="e.g. Halifax tour stop" /></Field>
        <Field label="Kind">
          <input list="mk-kinds" value={f.kind || ''} onChange={set('kind')} placeholder="Ad, email, tour stop, or your own" />
          <datalist id="mk-kinds">{KIND_SUGGESTIONS.map(k => <option key={k} value={k} />)}</datalist>
        </Field>
        <Field label="Due date"><input type="date" value={f.dueOn || ''} onChange={set('dueOn')} /></Field>
        <Field label="Status"><select value={f.status} onChange={set('status')}>{Object.entries(D_STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}</select></Field>
        <Field label="Who's doing it"><input value={f.owner || ''} onChange={set('owner')} placeholder="e.g. Jenn, CB, Claude" /></Field>
        <Field label="Cost (CA$)"><input type="number" min="0" step="0.01" value={f.cost ?? ''} onChange={set('cost')} /></Field>
        {/* How often it's paid (Jenn, 2026-10-21). A repeating cost counts over the campaign's dates. */}
        <Field label="How often"><select value={f.costTerm || 'once'} onChange={set('costTerm')}>
          <option value="once">One time</option><option value="week">Every week</option><option value="month">Every month</option><option value="year">Every year</option>
        </select></Field>
        <Field label="Where it sends people (a 365reset.com page)" wide><input value={f.targetUrl || ''} onChange={set('targetUrl')} /></Field>
      </div>

      <div className="note">
        {f.shortUrl
          ? <>Tracked link: <b>{f.shortUrl}</b> <button type="button" className="btn s" style={{ marginLeft: 8 }} onClick={copy}>{copied ? 'Copied' : 'Copy'}</button></>
          : <>Its tracked link is made for you when you save.</>}
        {' '}A cost here goes into the marketing costs on Goals &amp; plan by itself. A repeating cost counts once for each week, month or year between the campaign's start and end dates.
      </div>

      <p className="q" style={{ fontSize: 15, marginTop: 16 }}>What's involved</p>
      {f.items.length === 0 && <p className="qs">Nothing listed yet: merchandise, a booth, pictures, printing…</p>}
      {f.items.map((it, i) => (
        <div key={i} className="row" style={{ gap: 8, marginTop: 4 }}>
          <input type="checkbox" checked={it.done} onChange={() => setItems(xs => xs.map((x, j) => (j === i ? { ...x, done: !x.done } : x)))} aria-label={`${it.label} ready`} style={{ width: 18, height: 18 }} />
          <span style={{ textDecoration: it.done ? 'line-through' : 'none', flex: 1 }}>{it.label}</span>
          <button type="button" className="btn s" onClick={() => setItems(xs => xs.filter((_, j) => j !== i))} aria-label={`Remove ${it.label}`}>Remove</button>
        </div>
      ))}
      <div className="row" style={{ marginTop: 8 }}>
        <input value={newItem} onChange={e => setNewItem(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }}
          placeholder="Add something it needs" style={{ flex: 1, minWidth: 200, padding: 10, border: '1px solid var(--line)', borderRadius: 10 }} />
        <button type="button" className="btn s" onClick={addItem}>Add</button>
      </div>

      <p className="q" style={{ fontSize: 15, marginTop: 16 }}>Pictures and files</p>
      {f.files.length > 0 && (
        <div className="row" style={{ marginBottom: 8 }}>
          {f.files.map((x, i) => (
            <span key={i} className="tag" style={{ display: 'inline-flex', gap: 6, alignItems: 'center', padding: '4px 10px' }}>
              {/\.(jpe?g|png|gif|webp)(\?|$)/i.test(x.url) && <img src={x.url} alt="" style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 4 }} />}
              <a href={x.url} target="_blank" rel="noopener noreferrer">{x.name}</a>
              <button type="button" onClick={() => setF(y => ({ ...y, files: y.files.filter((_, j) => j !== i) }))} aria-label={`Remove ${x.name}`} style={{ border: 0, background: 'none', cursor: 'pointer' }}>×</button>
            </span>
          ))}
        </div>
      )}
      <div className="grid g2">
        <ImageDropZone onUploaded={(url) => setF(x => ({ ...x, files: [...x.files, { name: 'Picture', url }] }))} />
        <div>
          <div className="qs" style={{ marginBottom: 6 }}>Or add a link (Canva, Google Drive, a shop page…)</div>
          <div className="ws">
            <input value={link.name} onChange={e => setLink(l => ({ ...l, name: e.target.value }))} placeholder="Name" style={{ padding: 10, border: '1px solid var(--line)', borderRadius: 10 }} />
            <input value={link.url} onChange={e => setLink(l => ({ ...l, url: e.target.value }))} placeholder="https://…" style={{ padding: 10, border: '1px solid var(--line)', borderRadius: 10 }} />
          </div>
          <button type="button" className="btn s" style={{ marginTop: 8 }} onClick={addLink}>Add link</button>
        </div>
      </div>

      <p className="q" style={{ fontSize: 15, marginTop: 16 }}>Social posts that belong to it</p>
      <p className="qs">Their clicks, visits and joins count toward this piece.</p>
      <div className="row">
        {f.socialLinkCodes.map(c => {
          const p = posts.find(x => x.code === c);
          return (
            <span key={c} className="tag g" style={{ padding: '4px 10px' }}>
              {p ? `${p.label} · ${new Date(p.postAt).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}` : c}
              <button type="button" onClick={() => setF(x => ({ ...x, socialLinkCodes: x.socialLinkCodes.filter(y => y !== c) }))} aria-label="Remove post" style={{ border: 0, background: 'none', cursor: 'pointer', marginLeft: 4 }}>×</button>
            </span>
          );
        })}
        {unattached.length > 0 && (
          <select value="" onChange={e => { const c = e.target.value; if (c) setF(x => ({ ...x, socialLinkCodes: [...x.socialLinkCodes, c] })); }}
            style={{ padding: 8, border: '1px solid var(--line)', borderRadius: 10 }} aria-label="Attach a social post">
            <option value="">+ Attach a post…</option>
            {unattached.map(p => <option key={p.code} value={p.code}>{p.label} · {new Date(p.postAt).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}</option>)}
          </select>
        )}
      </div>

      <Field label={<span style={{ marginTop: 16, display: 'block' }}>Notes</span>}><GrowBox minRows={4} value={f.notes || ''} onChange={set('notes')} /></Field>

      {err && <div className="note">{err}</div>}
      <div className="row" style={{ marginTop: 14, justifyContent: 'space-between' }}>
        <div className="row">
          <button type="button" className="btn p" disabled={busy} onClick={submit}>{busy ? 'Saving…' : isNew ? 'Add piece' : 'Save'}</button>
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        </div>
        {onDelete && <button type="button" className="btn s" onClick={onDelete}>Remove piece</button>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The ideas list (marketing / advertising / publicity), grown over time
// ---------------------------------------------------------------------------

export function IdeasList({ onPick }) {
  const [ideas, setIdeas] = useState(null);
  const [editing, setEditing] = useState(null); // idea id, or 'new'
  const [err, setErr] = useState(null);
  const load = useCallback(() => { api.get('/admin/sales-marketing/ideas').then(r => setIdeas(r.data.ideas)).catch(() => setErr('Could not load the ideas list.')); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (idea, body) => {
    if (idea === 'new') await api.post('/admin/sales-marketing/ideas', body);
    else await api.put(`/admin/sales-marketing/ideas/${idea}`, body);
    setEditing(null); load();
  };

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div>
          <p className="q">Ideas list</p>
          <p className="qs">{onPick ? 'Pick one to add it as a piece, with what it involves filled in.' : 'Marketing, advertising and publicity ideas. We add to it as we go.'}</p>
        </div>
        {editing !== 'new' && <button type="button" className="btn s" onClick={() => setEditing('new')}>+ Add an idea</button>}
      </div>
      {err && <div className="note">{err}</div>}
      {editing === 'new' && <IdeaForm onCancel={() => setEditing(null)} onSave={(b) => save('new', b)} />}
      {ideas && (
        <div className="grid g3" style={{ marginTop: 10 }}>
          {GROUPS.map(([g, label]) => (
            <div key={g}>
              <h4 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 8px', color: 'var(--ink-2)' }}>{label}</h4>
              {ideas.filter(i => i.grp === g).map(i => (editing === i.id ? (
                <IdeaForm key={i.id} initial={i} onCancel={() => setEditing(null)} onSave={(b) => save(i.id, b)}
                  onRemove={async () => { if (!window.confirm(`Take "${i.name}" off the list?`)) return; await api.delete(`/admin/sales-marketing/ideas/${i.id}`); setEditing(null); load(); }} />
              ) : (
                <div key={i.id} style={{ border: '1px solid var(--line)', borderRadius: 12, padding: 12, marginBottom: 8 }}>
                  <b>{i.name}</b>
                  {i.involves.length > 0 && <div className="qs" style={{ margin: '4px 0 0' }}>{i.involves.join(' · ')}</div>}
                  {i.note && <div className="qs" style={{ margin: '4px 0 0' }}>{i.note}</div>}
                  <div className="row" style={{ marginTop: 8, gap: 6 }}>
                    {onPick && <button type="button" className="btn p s" onClick={() => onPick(i)}>Use this</button>}
                    <button type="button" className="btn s" onClick={() => setEditing(i.id)}>Edit</button>
                  </div>
                </div>
              )))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IdeaForm({ initial, onSave, onCancel, onRemove }) {
  const [f, setF] = useState({ grp: initial?.grp || 'marketing', name: initial?.name || '', involves: (initial?.involves || []).join('\n'), note: initial?.note || '' });
  const [err, setErr] = useState(null);
  const submit = async () => { setErr(null); try { await onSave({ ...f, involves: f.involves.split('\n') }); } catch (e) { setErr(errMsg(e)); } };
  return (
    <div style={{ border: '1px solid var(--terra-2)', borderRadius: 12, padding: 12, margin: '8px 0' }}>
      <div className="ws">
        <Field label="Idea"><input value={f.name} onChange={e => setF(x => ({ ...x, name: e.target.value }))} /></Field>
        <Field label="Group"><select value={f.grp} onChange={e => setF(x => ({ ...x, grp: e.target.value }))}>{GROUPS.map(([g, l]) => <option key={g} value={g}>{l}</option>)}</select></Field>
        <Field label="What's involved (one per line)" wide><textarea value={f.involves} onChange={e => setF(x => ({ ...x, involves: e.target.value }))} /></Field>
        <Field label="Note (optional)" wide><input value={f.note} onChange={e => setF(x => ({ ...x, note: e.target.value }))} /></Field>
      </div>
      {err && <div className="note">{err}</div>}
      <div className="row" style={{ marginTop: 10, justifyContent: 'space-between' }}>
        <div className="row"><button type="button" className="btn p s" onClick={submit}>Save idea</button><button type="button" className="btn s" onClick={onCancel}>Cancel</button></div>
        {onRemove && <button type="button" className="btn s" onClick={onRemove}>Take off the list</button>}
      </div>
    </div>
  );
}

